import { useMemo, useEffect, useRef } from "react";
import { useDashboard } from "./useDashboard";
import {
  type FilterPeriod,
  type CampaignOption,
} from "../components/dashboard/CampaignSummary/utils";

// Helper function to transform campaign names for campaign summary display
const transformCampaignName = (name: string): string => {
  // If name is empty, null, undefined, or just whitespace, use a fallback
  if (!name || name.trim() === "") {
    return "טריגר ללא שם";
  }

  if (name === "חיפוש") {
    return "תוצאה ישירה מהחיפוש";
  }
  return name;
};

interface UseCampaignSummaryFilterParams {
  activeFilter: FilterPeriod | undefined;
  selectedMonth: number | undefined;
}

export const useCampaignSummaryFilter = ({
  activeFilter,
  selectedMonth,
}: UseCampaignSummaryFilterParams) => {
  const {
    triggers,
    triggersYear,
    triggersWeek,
    triggersDay,
    triggersForever,
    viewsYear,
    loading,
    refetchForeverViewsTriggersData,
  } = useDashboard();

  // Track the last filter combination we attempted to fetch for
  const lastFetchKeyRef = useRef<string | null>(null);

  // Fetch forever data when "all" filter is selected and no month is selected
  useEffect(() => {
    const fetchKey = `${activeFilter}-${selectedMonth}`;
    const shouldFetch =
      (activeFilter === "all" || activeFilter === undefined) &&
      !selectedMonth &&
      (!triggersForever || triggersForever.length === 0) &&
      !loading &&
      lastFetchKeyRef.current !== fetchKey;

    if (shouldFetch) {
      lastFetchKeyRef.current = fetchKey;
      refetchForeverViewsTriggersData();
    }
  }, [
    activeFilter,
    selectedMonth,
    triggersForever,
    loading,
    refetchForeverViewsTriggersData,
  ]);

  const filteredOptions = useMemo((): CampaignOption[] => {
    // If a month is selected, use yearly data filtered by month
    if (selectedMonth !== undefined && viewsYear && viewsYear.length > 0) {
      // Find the data for the selected month
      const monthData = viewsYear.find((monthData) => {
        const date = new Date(monthData.date);
        const monthIndex = date.getMonth() + 1; // Convert to 1-12
        return monthIndex === selectedMonth;
      });

      if (monthData && monthData.triggers) {
        const options = monthData.triggers
          .map((trigger) => ({
            id: trigger.id,
            label: transformCampaignName(trigger.name),
            value: trigger.joins,
          }))
          .sort((a, b) => b.value - a.value);

        return options;
      }
    }

    // Use specific period data based on active filter
    if (activeFilter === "day") {
      if (triggersDay && triggersDay.length > 0) {
        const options = triggersDay
          .map((trigger) => ({
            id: trigger.id,
            label: transformCampaignName(trigger.name),
            value: trigger.joins,
          }))
          .sort((a, b) => b.value - a.value);
        return options;
      }
      // Return empty array if no day data
      return [];
    }

    if (activeFilter === "week") {
      if (triggersWeek && triggersWeek.length > 0) {
        const options = triggersWeek
          .map((trigger) => ({
            id: trigger.id,
            label: transformCampaignName(trigger.name),
            value: trigger.joins,
          }))
          .sort((a, b) => b.value - a.value);
        return options;
      }
      // Return empty array if no week data
      return [];
    }

    // For "all" filter, use forever data if available, otherwise fall back to yearly data
    if (
      !selectedMonth &&
      (activeFilter === "all" || activeFilter === undefined)
    ) {
      // First try to use forever data
      if (triggersForever && triggersForever.length > 0) {
        const options = triggersForever
          .map((trigger) => ({
            id: trigger.id,
            label: transformCampaignName(trigger.name),
            value: trigger.joins,
          }))
          .sort((a, b) => b.value - a.value);

        return options;
      }

      // Fallback to yearly data if forever data is not available
      if (triggersYear && triggersYear.length > 0) {
        const options = triggersYear
          .map((trigger) => ({
            id: trigger.id,
            label: transformCampaignName(trigger.name),
            value: trigger.joins,
          }))
          .sort((a, b) => b.value - a.value);

        return options;
      }
      // Return empty array if no data available
      return [];
    }

    // Fallback to all-time triggers
    if (!triggers || triggers.length === 0) {
      return [];
    }

    const options = triggers
      .map((trigger) => ({
        id: trigger.id,
        label: transformCampaignName(trigger.name),
        value: trigger.joins,
      }))
      .sort((a, b) => b.value - a.value);

    // For "all" filter, return all triggers
    return options;
  }, [
    triggers,
    triggersYear,
    triggersWeek,
    triggersDay,
    viewsYear,
    activeFilter,
    selectedMonth,
  ]);

  return {
    options: filteredOptions,
    loading,
    hasData: filteredOptions.length > 0,
  };
};
